// Package imports
import { useEffect, useState } from "react";
import { useToast, Menu, Pressable, HStack, Text, View } from "native-base";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
// components
import NumberedSummaryBox from "atoms/NumberedSummaryBox";
import PaginationMui from "molecules/Pagination";
import CustomTable from "molecules/CustomTable";
import NoData from "atoms/NoData";
import { fetchSdRequests } from "api/apiCall";
import TeamModal from "molecules/TeamModal";
import CreateModal from "molecules/CreateModal";
// utilities
import { SD_DETAILS } from "global/paths";
import { showErrorToast } from "utils/utilities";
import { getStore } from "store/storeUtils";
import colors from "theme/colors";
import { AiOutlineDownload } from "react-icons/ai";
import { CREATE_MODALS } from "global/constants";

const SDRequestsList = ({ isMinimize }) => {
  const toast = useToast();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { modal, setModal, resetLoginData } = getStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [totalPageNumber, setTotalPageNumber] = useState(0);

  const {
    isLoading,
    mutate: getSdRequests,
    isSuccess,
    data: sdRequestsData,
    isError: isSdRequestsError,
    error: sdRequestsError,
  } = useMutation({ mutationFn: (payload) => fetchSdRequests(payload) });

  useEffect(() => {
    if (isSdRequestsError) {
      showErrorToast({
        error: sdRequestsError,
        toast,
        navigate,
        resetLoginData,
      });
    }
  }, [isSdRequestsError, sdRequestsError]);

  useEffect(() => {
    if (isSuccess) {
      setTotalPageNumber(Math.ceil((sdRequestsData?.count || 0) / 50));
      setCurrentData(sdRequestsData?.data);
    }
  }, [isSuccess, sdRequestsData]);

  useEffect(() => {
    const skipElements = currentPage == 1 ? 0 : 50 * (currentPage - 1);
    getSdRequests({ limit: 50, skip: skipElements });
  }, [currentPage]);

  const onDownloadPress = () => {
    setModal({
      ...modal,
      type: CREATE_MODALS.exportAllSDRequests,
    });
  };

  return (
    <>
      <NumberedSummaryBox
        number="3"
        title={t("salaryDisbursement.list.title")}
        maxWidth="1600px"
      >
        <Menu
          trigger={(triggerProps) => {
            return (
              <Pressable
                w="190px"
                px="4"
                pt="4"
                accessibilityLabel="SD Landing Page Download"
                {...triggerProps}
              >
                <HStack
                  justifyContent="center"
                  space="2"
                  borderWidth={1}
                  borderColor={colors.disabled.smoke}
                  borderRadius={4}
                  padding={2}
                >
                  <Text fontSize="xs">
                    {t("salaryDisbursement.list.header.download")}
                  </Text>
                  <AiOutlineDownload size="17px" />
                </HStack>
              </Pressable>
            );
          }}
        >
          <Menu.Item onPress={onDownloadPress}>
            {t("transactionPage.downloadAllData")}
          </Menu.Item>
        </Menu>
        <View pb="4" />
        {(currentData?.length || 0) === 0 ? (
          <NoData isSDLanding />
        ) : (
          <>
            <CustomTable
              isMinimize={isMinimize}
              data={currentData}
              isLoading={isLoading}
              isPressable
              redirectTo={SD_DETAILS}
              skip={currentPage == 1 ? 0 : 50 * (currentPage - 1)}
              headers={[
                "salaryDisbursement.list.header.no",
                "salaryDisbursement.list.header.schedulingNumber",
                "salaryDisbursement.list.header.uploadDate",
                "salaryDisbursement.list.header.totalNominal",
                "salaryDisbursement.list.header.verificationStatus",
                "salaryDisbursement.list.header.rejectionReason",
                "salaryDisbursement.list.header.disbursementStatus",
                "salaryDisbursement.list.header.scheduledOn",
                "salaryDisbursement.list.header.download",
              ]}
              fields={[
                "no",
                "id",
                "createdAt",
                "amount",
                "requestStatus",
                "rejectionReason",
                "disbursementStatus",
                "expectedDisbursementDate",
                "download",
              ]}
            />
            <PaginationMui
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPageNumber={totalPageNumber}
            />
          </>
        )}
      </NumberedSummaryBox>
      <TeamModal />
      <CreateModal />
    </>
  );
};

export default SDRequestsList;
